import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import 'react-datepicker/dist/react-datepicker.css';
import './RemoverVacaciones.css'; // We'll create this file next

function RemoverVacaciones() {
    const navigate = useNavigate();
    const [currentView, setCurrentView] = useState('calendar'); // 'calendar', 'exceptions', or 'confirmation'
    const [selectedDate, setSelectedDate] = useState(null);
    const [searchEmail, setSearchEmail] = useState('@wsp.com.mx');
    const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Function to filter weekends
    const isWeekday = (date) => {
        const day = date.getDay();
        return day !== 0 && day !== 6;
    };

    // Handle date selection
    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    // Handle next button in calendar view
    const handleCalendarNext = () => {
        if (selectedDate) {
            setCurrentView('exceptions');
        }
    };

    // Handle email search for suggestions
    const handleEmailSearch = async (e) => {
        const value = e.target.value;
        setSearchEmail(value);

        if (value.length >= 3) {
            try {
                const { data, error } = await supabase
                    .from('employees')
                    .select('id, full_name, wsp_email')
                    .ilike('wsp_email', `%${value}%`)
                    .limit(5);

                if (error) throw error;
                setEmployeeSuggestions(data);
            } catch (error) {
                console.error('Error fetching suggestions:', error);
            }
        } else {
            setEmployeeSuggestions([]);
        }
    };

    // Add employee to exceptions list
    const addException = async () => {
        if (!searchEmail.includes('@wsp.com.mx')) {
            setError('Por favor ingrese un correo WSP válido');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('employees')
                .select('id, full_name, wsp_email')
                .eq('wsp_email', searchEmail.toLowerCase())
                .single();

            if (error || !data) {
                setError('No se encontró el empleado con ese correo');
                return;
            }

            // Check if employee is already in exceptions
            if (exceptions.some(emp => emp.id === data.id)) {
                setError('Este empleado ya está en la lista de excepciones');
                return;
            }

            setExceptions([...exceptions, data]);
            setSearchEmail('@wsp.com.mx');
            setEmployeeSuggestions([]);
            setError('');
        } catch (error) {
            console.error('Error adding exception:', error);
            setError('Error al agregar la excepción');
        }
    };

    // Remove employee from exceptions list
    const removeException = (id) => {
        setExceptions(exceptions.filter(emp => emp.id !== id));
    };

    // Handle final confirmation
    const handleConfirmation = async () => {
        setLoading(true);
        setError('');

        try {
            // 1. Get all active employees with their overtime data
            const { data: employees, error: employeesError } = await supabase
                .from('employees')
                .select('id, remaining_days, days_taken')
                .eq('active', true);

            if (employeesError) throw employeesError;

            // 2. Get all vacation days for the selected date
            const { data: existingVacationDays, error: vacationDaysError } = await supabase
                .from('vacation_days')
                .select('employee_id')
                .eq('vacation_date', selectedDate.toISOString().split('T')[0]);

            if (vacationDaysError) throw vacationDaysError;

            // Create a set of employee IDs who already have this day registered
            const employeesWithVacation = new Set(existingVacationDays.map(v => v.employee_id));

            // 3. Filter out exceptions and employees who already have vacation
            const exceptionIds = exceptions.map(emp => emp.id);
            const employeesToUpdate = employees.filter(emp => 
                !exceptionIds.includes(emp.id) && !employeesWithVacation.has(emp.id)
            );

            // 4. For each employee to update, handle both regular and overtime days
            for (const emp of employeesToUpdate) {
                // First, check if employee has overtime days
                const { data: overtimeData } = await supabase
                    .from('overtime_vacation_days')
                    .select('days_remaining')
                    .eq('employee_id', emp.id)
                    .single();

                // Update regular vacation days
                await supabase
                    .from('employees')
                    .update({
                        remaining_days: emp.remaining_days - 1,
                        days_taken: (emp.days_taken || 0) + 1
                    })
                    .eq('id', emp.id);

                // Handle overtime days if they exist
                if (overtimeData && overtimeData.days_remaining !== null) {
                    if (overtimeData.days_remaining <= 1) {
                        // If this was their last overtime day, set to null
                        await supabase
                            .from('overtime_vacation_days')
                            .update({
                                days_remaining: null,
                                expiration_date: null
                            })
                            .eq('employee_id', emp.id);
                    } else {
                        // Otherwise decrease by 1
                        await supabase
                            .from('overtime_vacation_days')
                            .update({
                                days_remaining: overtimeData.days_remaining - 1
                            })
                            .eq('employee_id', emp.id);
                    }
                }

                // Add vacation day entry
                await supabase
                    .from('vacation_days')
                    .insert({
                        employee_id: emp.id,
                        vacation_date: selectedDate.toISOString().split('T')[0]
                    });
            }

            setConfirmationMessage(
                `Se ha registrado el día ${formatDate(selectedDate)} como vacación para todos los empleados elegibles.` +
                `\nSe excluyeron ${exceptionIds.length} empleados por excepción y ${employeesWithVacation.size} empleados que ya tenían este día registrado.`
            );
            setCurrentView('confirmation');
        } catch (error) {
            console.error('Error processing vacation day removal:', error);
            setError('Ocurrió un error al procesar la solicitud. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
            setShowConfirmDialog(false);
        }
    };

    // Format date for display
    const formatDate = (date) => {
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        return date.toLocaleDateString('es-MX', options).replace(',', ' del');
    };

    // Render the calendar view
    const renderCalendarView = () => (
        <div className="remover-vacaciones-container">
            <h1>Escoge el día que se le dará a todos de vacaciones</h1>
            <div className="calendar-wrapper">
                <DatePicker
                    inline
                    filterDate={isWeekday}
                    selected={selectedDate}
                    onChange={handleDateSelect}
                    highlightDates={selectedDate ? [selectedDate] : []}
                />
            </div>
            <button
                className="submit-button"
                disabled={!selectedDate}
                onClick={handleCalendarNext}
            >
                Siguiente
            </button>

            {selectedDate && (
                <div className="selected-date-preview">
                    Has seleccionado el día: <strong>{formatDate(selectedDate)}</strong>
                </div>
            )}
        </div>
    );

    // Render the exceptions view
    const renderExceptionsView = () => (
        <div className="remover-vacaciones-container">
            <h1>Selecciona los empleados que sí trabajarán el {formatDate(selectedDate)}</h1>
            <p className="exceptions-helper">
                Estos empleados no tendrán este día como vacación y no se les descontará un día de sus vacaciones disponibles.
            </p>

            <div className="email-search-container">
                <div className="email-input-wrapper">
                    <input
                        type="text"
                        placeholder="Buscar por correo WSP"
                        value={searchEmail}
                        onChange={handleEmailSearch}
                        className="email-search-input"
                    />
                    <button
                        className="add-exception-button"
                        onClick={addException}
                    >
                        Agregar
                    </button>
                </div>

                {employeeSuggestions.length > 0 && (
                    <div className="suggestions-container">
                        {employeeSuggestions.map(emp => (
                            <div
                                key={emp.id}
                                className="suggestion-item"
                                onClick={() => {
                                    setSearchEmail(emp.wsp_email);
                                    setEmployeeSuggestions([]);
                                }}
                            >
                                {emp.full_name} ({emp.wsp_email})
                            </div>
                        ))}
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}
            </div>

            <div className="exceptions-list">
                <h2>Empleados que trabajarán este día:</h2>
                {exceptions.length === 0 ? (
                    <p className="no-exceptions">No hay empleados exceptuados</p>
                ) : (
                    <ul>
                        {exceptions.map(emp => (
                            <li key={emp.id} className="exception-item">
                                <span>{emp.full_name} ({emp.wsp_email})</span>
                                <button
                                    className="remove-exception-button"
                                    onClick={() => removeException(emp.id)}
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="buttons-container">
                <button
                    className="back-button"
                    onClick={() => setCurrentView('calendar')}
                >
                    Regresar
                </button>
                <button
                    className="submit-button"
                    onClick={() => setShowConfirmDialog(true)}
                >
                    Confirmar
                </button>
            </div>

            {showConfirmDialog && (
                <div className="confirm-dialog-overlay">
                    <div className="confirm-dialog">
                        <h3>¿Está seguro de que desea continuar?</h3>
                        <p>Esta acción no se puede deshacer en masa.</p>
                        <p>Si comete un error, este tendrá que remediarse de manera manual.</p>
                        <div className="confirm-dialog-buttons">
                            <button
                                className="cancel-button"
                                onClick={() => setShowConfirmDialog(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="confirm-button"
                                onClick={handleConfirmation}
                                disabled={loading}
                            >
                                {loading ? 'Procesando...' : 'Sí, continuar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Render the confirmation view
    const renderConfirmationView = () => (
        <div className="remover-vacaciones-container confirmation-view">
            <div className="confirmation-content">
                <h1>¡Proceso Completado!</h1>
                <p className="confirmation-message">{confirmationMessage}</p>

                <div className="confirmation-details">
                    <h3>Detalles de la operación:</h3>
                    <p>Día registrado como vacación: <strong>{formatDate(selectedDate)}</strong></p>
                    <p>Empleados exceptuados: <strong>{exceptions.length}</strong></p>
                </div>

                {exceptions.length > 0 && (
                    <div className="exceptions-summary">
                        <h3>Empleados que trabajarán ese día:</h3>
                        <ul>
                            {exceptions.map(emp => (
                                <li key={emp.id}>{emp.full_name}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <button
                    className="submit-button"
                    onClick={() => navigate('/dashboard')}
                >
                    Volver al Tablero
                </button>
            </div>
        </div>
    );

    return (
        <div className="remover-vacaciones">
            <Navbar />
            <div className="content">
                {currentView === 'calendar' && renderCalendarView()}
                {currentView === 'exceptions' && renderExceptionsView()}
                {currentView === 'confirmation' && renderConfirmationView()}
            </div>
        </div>
    );
}

export default RemoverVacaciones;